'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/i18n/routing';
import { useAuth } from '@/contexts/auth-context';
import { useFirstAccessibleRoute } from '@/hooks/use-nav';
import { useRouteAccess } from '@/hooks/use-route-access';
import { consumePendingRedirect } from '../utils/redirect';
import { Loader2 } from 'lucide-react';

export function GuestGuard({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const firstRoute = useFirstAccessibleRoute();
  const canAccess = useRouteAccess();

  useEffect(() => {
    if (status === 'authenticated') {
      const target = consumePendingRedirect(searchParams, firstRoute);
      // A remembered path belongs to whoever was signed in when it was stored,
      // and `?next=` can be anything a bookmark or a shared link carries. Land
      // on it only if THIS account can open it, else on its own first screen —
      // otherwise login ends on a full-page "Access Denied" (OMA-605).
      router.replace(canAccess(target) ? target : firstRoute);
    }
  }, [status, router, searchParams, firstRoute, canAccess]);

  if (status === 'loading') {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  if (status === 'authenticated') {
    return null;
  }

  return <>{children}</>;
}
